import { useMemo } from "react";
import { ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import { type Granularity, type TimeRange, type TimeRangePreset, PRESETS, autoGran, rangeFromPreset } from "@/lib/timeRange";
import { useNow } from "@/hooks/useNow";
import { formatRelativeAge } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/cn";

/**
 * TimeRangeBar — controls the start/end window + granularity for any
 * historical chart on the page.
 *
 * Layout (TB-EMS-style):
 *
 *   [‹] [from datetime] → [to datetime] [›]   [Last hour][6h][Today][24h][7d][30d][YTD]   [Granularity: 15min]
 *
 *   ‹ / ›  — shift the window backwards / forwards by its own span.
 *   from/to — native datetime-local inputs the operator can edit directly.
 *   Presets — quick-pick chips; clicking one sets a sane default granularity.
 *   Granularity — segmented control (no nested dropdown).
 *
 * The component is sticky under the topbar so it's always reachable when
 * scrolling through long charts.
 */
export function TimeRangeBar({
  range,
  onChange,
  granularities = ["raw", "15m", "h", "d", "w", "m", "y"],
  defaultPreset = "24h",
}: {
  range: TimeRange;
  onChange: (r: TimeRange) => void;
  granularities?: Granularity[];
  defaultPreset?: TimeRangePreset;
}) {
  const t = useT();
  // Preset clicks and Reset snap the preset's default granularity into
  // this page's allowlist, so the segmented control never de-selects
  // itself after a preset click (HIST-10).
  function snapGran(r: TimeRange): TimeRange {
    return granularities.includes(r.gran) ? r : { ...r, gran: autoGran(r.end - r.start, granularities) };
  }
  function presetClick(key: TimeRangePreset) {
    onChange(snapGran(rangeFromPreset(key)));
  }
  function shift(direction: -1 | 1) {
    const span = range.end - range.start;
    onChange({ ...range, start: range.start + direction * span, end: range.end + direction * span, preset: "custom" });
  }
  function reset() { onChange(snapGran(rangeFromPreset(defaultPreset))); }

  function setFrom(s: string) {
    const t = parseLocal(s);
    if (!Number.isFinite(t) || t >= range.end) return;
    const gran = autoGran(range.end - t, granularities);
    onChange({ ...range, start: t, gran, preset: "custom" });
  }
  function setTo(s: string) {
    const t = parseLocal(s);
    if (!Number.isFinite(t) || t <= range.start) return;
    const gran = autoGran(t - range.start, granularities);
    onChange({ ...range, end: t, gran, preset: "custom" });
  }

  const fromVal = useMemo(() => formatLocal(range.start), [range.start]);
  const toVal = useMemo(() => formatLocal(range.end), [range.end]);
  const spanLabel = formatSpan(range.end - range.start);

  // Staleness badge: a frozen forensic window (custom preset whose end is
  // more than 5 min in the past) is visibly frozen, not silently stale
  // (HIST-03). Relative presets auto-follow now, so they never show it.
  // The " ago" suffix is stripped so the dict key can place its own
  // language's equivalent ("termina {age} fa", "endet vor {age}").
  const now = useNow(30_000);
  const endsAgo = range.preset === "custom" && now - range.end > 5 * 60_000
    ? formatRelativeAge(now - range.end).replace(/ ago$/, "")
    : null;
  const resetLabel = t(`analyze.preset.${defaultPreset}`);

  return (
    <div className="sticky top-14 z-20 -mx-6 mb-3 border-y border-edge bg-bg/95 px-6 py-2 backdrop-blur supports-[backdrop-filter]:bg-bg/80">
      <div className="flex flex-wrap items-center gap-2">
        {/* DATE RANGE */}
        <div className="flex items-center gap-1 rounded-md border border-edge bg-surface px-1">
          <button
            onClick={() => shift(-1)}
            className="grid h-7 w-7 place-items-center rounded text-text-muted hover:bg-surface-2 hover:text-text"
            title={t("analyze.range.shiftBack")}
          >
            <ChevronLeft size={14} />
          </button>
          <DateTimeInput value={fromVal} onCommit={setFrom} />
          <span className="mx-0.5 text-text-faint">→</span>
          <DateTimeInput value={toVal} onCommit={setTo} />
          <button
            onClick={() => shift(1)}
            className="grid h-7 w-7 place-items-center rounded text-text-muted hover:bg-surface-2 hover:text-text"
            title={t("analyze.range.shiftFwd")}
          >
            <ChevronRight size={14} />
          </button>
          <span className="hidden border-l border-edge pl-2 pr-1 text-[10px] text-text-faint sm:inline-block">
            {spanLabel}
          </span>
          {endsAgo && (
            <span
              className="pr-1 text-[10px] font-medium text-warn"
              title={t("analyze.range.endsAgoTitle")}
            >
              {t("analyze.range.endsAgo", { age: endsAgo })}
            </span>
          )}
        </div>

        {/* PRESETS */}
        <div className="flex items-center gap-0.5 rounded-md border border-edge bg-surface p-0.5">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => presetClick(p.key)}
              className={cn(
                "rounded px-2.5 py-1 text-[11px] font-medium text-text-muted transition hover:text-text",
                range.preset === p.key && "bg-accent-soft text-accent-strong",
              )}
            >
              {t(`analyze.preset.${p.key}`)}
            </button>
          ))}
        </div>

        {/* GRANULARITY SEGMENTED */}
        <div className="flex items-center gap-1 rounded-md border border-edge bg-surface p-0.5">
          <span className="px-1.5 text-[10px] uppercase tracking-wider text-text-faint">{t("analyze.range.gran")}</span>
          {granularities.map((g) => (
            <button
              key={g}
              onClick={() => onChange({ ...range, gran: g })}
              className={cn(
                "rounded px-2 py-1 text-[11px] font-medium text-text-muted transition hover:text-text",
                range.gran === g && "bg-accent-soft text-accent-strong",
              )}
              title={t(`analyze.granLabel.${g}`)}
            >
              {SHORT_GRAN[g]}
            </button>
          ))}
        </div>

        {/* RESET */}
        <button
          onClick={reset}
          className="flex h-8 items-center gap-1 rounded-md border border-edge bg-surface px-2 text-[11px] text-text-muted hover:text-text"
          title={t("analyze.range.resetTo", { preset: resetLabel })}
        >
          <RotateCcw size={11} /> {t("analyze.range.reset")}
        </button>
      </div>
    </div>
  );
}

/** Inline datetime input — edits commit on blur or Enter. */
function DateTimeInput({ value, onCommit }: { value: string; onCommit: (v: string) => void }) {
  return (
    <input
      type="datetime-local"
      defaultValue={value}
      key={value}                       // resync when parent changes (preset click)
      onBlur={(e) => onCommit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
      className="h-7 rounded border border-transparent bg-transparent px-1 text-[11px] num tabular-nums text-text outline-none hover:border-edge focus:border-accent focus:bg-surface-2"
    />
  );
}

/* Single-letter labels for the granularity segmented control. */
const SHORT_GRAN: Record<Granularity, string> = {
  raw: "raw",
  "2m": "2 m",
  "5m": "5 m",
  "15m": "15 m",
  h: "1 h",
  d: "1 d",
  w: "1 w",
  m: "1 M",
  y: "1 Y",
};

function parseLocal(s: string): number {
  // datetime-local format: "YYYY-MM-DDTHH:mm" (browser-local). Date()
  // parses it relative to the user's tz which is what we want here.
  const d = new Date(s);
  return d.getTime();
}

function formatLocal(ms: number): string {
  if (!Number.isFinite(ms)) return "";
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function formatSpan(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "";
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins} min`;
  const hours = mins / 60;
  if (hours < 48) return `${hours.toFixed(hours < 10 ? 1 : 0)} h`;
  const days = hours / 24;
  if (days < 90) return `${days.toFixed(days < 10 ? 1 : 0)} d`;
  return `${(days / 30).toFixed(1)} mo`;
}
